import React from "react"
import { useIntl } from "react-intl"
import { Box, Divider, Progress, Stack, Typography } from "@carrotfertility/carotene-core"
import { useAuthentication } from "#/components/context/authentication/AuthenticationProvider"
import { useSubscriptionStatus } from "#/services/user/hooks/useSubscriptionStatus"
import { UserDropdown } from "#/features/global-ui/user-dropdown/user-dropdown"
import { AEONIK_FONT_URLS } from "#/fonts/aeonikFontUrls"
import {
  isExternalStorefrontMember,
  isLapsedExternalStorefrontMember
} from "#/services/consumerSubscription/externalStorefront"
import { LapsedExternalStorefrontHandoff } from "#/components/views/LapsedExternalStorefrontHandoff"
import { ExternalStorefrontSubscriptionLinks } from "#/components/views/account/ExternalStorefrontSubscriptionLinks"

// Billing with NO global layout at all — not `NavLayout`, not `PageLayout` (CONAPP-452).
//
// ── WHY THERE IS NO CHROME, which is the part worth not re-deriving ──────────────
// `/api/v2/users/is-logged-in` answers `isLoggedIn: false` for a `renewal_required`
// member. That is not a bug and not a missing session: the cookie is valid, but the
// activation cascade has not granted the Employee UserRole, and carrot-app gates some
// data on it (CAP-142 grants the role, CAP-156 reissues the cookie with it — see the
// note on `handleProbeAppAccess` in Root.tsx).
//
// So redux `isLoggedIn` is false, `AppContainer` never mounts, and there IS no
// authenticated app session for chrome to describe. A sidebar would link to pages the
// backend will refuse, and an account dropdown would hang off a session the app does not
// believe in. Hence: no layout container, just the page.
//
// This is ALSO why the first attempt at this was dead code. It mounted a chromeless route
// inside `App.tsx`, which lives under Root's `loggedIn ? AppContainer : …` split — so for
// the one member it was built for, it could never render. The route has to be resolved in
// Root, above that split.
//
// A caller with NO session still gets the login screen — see the note on the session probe
// below. `!loggedIn` alone is not enough to earn this page.
//
// ── The session IS real, so the reads are enabled ───────────────────────────────
// `isLoggedIn` is passed as `true` deliberately, and it is not a lie about redux: that
// prop gates the package's AUTHED BFF READS, and the cookie those travel on works. The
// billing summary, the card and the receipts are exactly the data a lapsed member is here
// for. Passing redux's `false` would disable every read and render an empty page.

// ── WHO GETS THIS PAGE vs THE LOGIN SCREEN ──────────────────────────────────────
// `is-logged-in` answers `isLoggedIn: false` to BOTH "no session" and "session without
// the Employee UserRole", so it cannot tell a lapsed member from a stranger. The
// discriminator is whether the cookie authenticates to the BFF, which is also exactly the
// property that decides whether this page can function at all.
//
// `subscription.getStatus` answers that in one cheap call. It is a `protectedProcedure`
// (requireSession + requireCookie), so a stranger gets UNAUTHORIZED, while a lapsed
// member's cookie authenticates fine — which is why this page renders their real billing
// data today. The call is not extra work: it shares `SUBSCRIPTION_STATUS_QUERY_KEY` with
// `useIsPaywalled`, so the answer is already cached for the app if they restart billing.
//
// A non-401 failure renders the PAGE, not the login screen. Bouncing a lapsed member to
// login on a network blip is the worse error, and the page degrades into its own error
// states with sign-out still reachable — the same fail-open direction `useIsPaywalled`
// takes.
//
// ⛔ The verdict is LATCHED in local state, and that is load-bearing rather than tidy — see
// `sessionRejected` below. Re-deriving it from the query each render is an infinite loop.

// Matches `PageLayout`'s cap (60.5rem), so the measure is the same one the in-chrome
// version uses and the billing card is not re-tuned for this surface.
const CONTENT_MAX_INLINE_SIZE = "60.5rem"

// ── GETTING THE CHROME BACK AFTER A SUCCESSFUL RENEWAL ──────────────────────────
// Paying does not, on its own, end the condition that put the member on this page.
// `is-logged-in` is what Root gates the app on, and it only flips once the cascade
// behind the payment finishes: Stripe webhook → Convex schedules the CI eligibility
// PATCH → `Employee.Active` + the Employee UserRole → the next request reissues the
// cookie with it. All asynchronous, so "paid" lands before "has app access" — which is
// why this polls rather than acting on the payment result.
//
// Same cadence and bound as the funnel's post-payment wait (SubscriptionConfirmation),
// because it is the same cascade. Slightly longer bound: nothing is gated on it here —
// the member is already reading their renewed billing state — so waiting costs them
// nothing, where the funnel was holding a CTA.
const APP_ACCESS_POLL_INTERVAL_MS = 2000
const APP_ACCESS_TIMEOUT_MS = 30000

const ConsumerSubscriptionBillingRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionBillingRoute }))
)

function httpStatusOf(error: unknown): number | undefined {
  return (error as { data?: { httpStatus?: number } } | null)?.data?.httpStatus
}

export type ConsumerBillingStandalonePageProps = {
  /**
   * What to render for a caller with no session at all — the login screen.
   *
   * Passed in rather than imported so Root keeps ownership of route composition: it is the
   * thing that decides what "unauthenticated" should look like at this URL, and it renders
   * the login page IN PLACE so the URL survives and billing paints as soon as sign-in
   * succeeds.
   */
  unauthenticatedFallback: React.ReactNode
  /**
   * Resolves true once carrot-app's own session gate recognises this member — the SAME
   * check redux gates the app on. Polled after a successful renewal to decide when the
   * full layout can come back. Host-owned (Root's `handleProbeAppAccess`) so there is one
   * implementation and one place explaining why it must be a bare READ and never a
   * dispatch.
   *
   * Optional — absent ⇒ the page simply stays chromeless until the member reloads, which
   * is the pre-existing behaviour.
   */
  onProbeAppAccess?: () => Promise<boolean>
}

export default function ConsumerBillingStandalonePage({
  unauthenticatedFallback,
  onProbeAppAccess
}: ConsumerBillingStandalonePageProps): JSX.Element {
  const intl = useIntl()

  // ── THE VERDICT IS LATCHED, and that fixed an infinite loop ─────────────────────────
  // Rendering the fallback does NOT unmount this component, so the probe's observer is still
  // live underneath the login screen — and `LoginContainer` clears the ENTIRE react-query
  // cache on mount, deliberately, so a previous member's data cannot outlive the landing
  // there. That wipe takes the 401 with it: the probe refetches, this page swaps back to its
  // spinner, the login screen unmounts, the next 401 mounts it again and it clears the cache
  // again — forever, one request per cycle. So a verdict about the session cannot be
  // re-derived from the cache the login screen is entitled to wipe.
  // (`AuthenticationProvider` guards the same invariant from the other side: read the comment
  // on its own `queryClient.clear()` teardown.)
  //
  // Set during render, not in an effect, so no frame renders the billing page to a stranger —
  // React re-renders before committing. Latching also turns the probe OFF, so the wipe starts
  // no refetch at all.
  //
  // Latching for the life of the mount is sound: both ways off this login form replace this
  // component outright — a successful sign-in flips `loggedIn` in Root, which swaps this route
  // to `AppContainer`, and a `renewal_required` response does a full page load.
  const [sessionRejected, setSessionRejected] = React.useState(false)
  const sessionProbe = useSubscriptionStatus(!sessionRejected)
  if (!sessionRejected && httpStatusOf(sessionProbe.error) === 401) {
    setSessionRejected(true)
  }

  const { handleSignOut } = useAuthentication()

  // ── Renewal → full app chrome ─────────────────────────────────────────────────────
  // The package reports the reactivation (from a server read, not the browser's payment
  // result); this page owns what to DO about it, because only the host knows how its
  // session state is bootstrapped.
  //
  // ⚠️ A FULL PAGE LOAD, not a redux refresh. Re-dispatching `fetchReduxData` here would
  // fire FETCH_IS_LOGGED_IN_REQUEST, which flips `loadingIsLoggedIn` — and Root's
  // `showPage` gate unmounts the ENTIRE app while that round trip runs. That is the
  // documented damage behind `handleConsumerLogin`, and it is why the funnel's
  // `handleExitToApp` is also a page load. Reloading re-runs `fetchReduxData` against the
  // now-entitled cookie, redux `isLoggedIn` becomes true, and Root renders this same route
  // through `AppContainer` — nav, top bar and the in-chrome billing page.
  //
  // Navigating to `pathname` rather than `reload()`: a 3DS return arrives here with
  // Stripe's `payment_intent_client_secret` on the query string, and reloading with it
  // still attached would re-run the redirect handler and re-toast a payment already
  // settled. Dropping the query lands on the clean billing URL.
  const [awaitingAppAccess, setAwaitingAppAccess] = React.useState(false)
  const handleReactivated = React.useCallback(() => setAwaitingAppAccess(true), [])

  // Held in a ref so the effect below can depend on `awaitingAppAccess` ALONE. Depending
  // on the callback identity would restart the loop — and stack a second timer — on any
  // Root re-render that hands down a fresh function.
  const probeRef = React.useRef(onProbeAppAccess)
  probeRef.current = onProbeAppAccess

  React.useEffect(() => {
    if (!awaitingAppAccess) return undefined
    const probe = probeRef.current
    if (probe === undefined) return undefined

    let cancelled = false
    let timer: number | undefined
    const deadline = Date.now() + APP_ACCESS_TIMEOUT_MS

    const poll = async (): Promise<void> => {
      // A throwing probe means "not ready yet", never a terminal answer — the request can
      // fail for reasons that have nothing to do with the cascade.
      const ready = await probe().catch(() => false)
      if (cancelled) return
      if (ready) {
        window.location.assign(window.location.pathname)
        return
      }
      // Giving up leaves the member on a page that WORKS and already shows their renewed
      // state — just without nav until they navigate or reload. Strictly better than
      // reloading on a false answer, which would land them right back here with no chrome
      // and no success toast.
      if (Date.now() >= deadline) return
      // Block body, not `() => void poll()`: a concise body has to infer its return type
      // from `poll`, which is still being inferred here — TS7011 under noImplicitAny.
      timer = window.setTimeout(() => {
        void poll()
      }, APP_ACCESS_POLL_INTERVAL_MS)
    }
    void poll()

    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [awaitingAppAccess])

  // ── THE ONE PIECE OF CHROME: a top bar whose menu holds sign-out and nothing else ──
  // Without it this page is a dead end: no nav, no account menu, and no way off it but the
  // browser. That was the gap raised on the PR for the earlier in-chrome attempt, and it
  // bites harder here, because the member arrives straight from login.
  //
  // Renders the PRESENTATIONAL `user-dropdown`, NOT the `UserDropdown` container beside it.
  // The container reads `useCurrentUser`, `useBenefitEnrollment`, `useShowBankDetails` and
  // `useMemberProgramFlags` — providers that live inside `AppContainer`, which never mounts
  // for this member — so it would throw here. The presentational component takes its items
  // as a prop, which is exactly the seam this needs.
  //
  // ONE ITEM, deliberately. Every other entry in the real menu (Account settings, Billing,
  // Bank details, …) is a route the backend refuses without the Employee UserRole, so
  // offering them would be offering dead ends.
  //
  // `name` is the generic "Account" and the initials are empty — the same fallback the real
  // container lands on for a member it has no names for, which this member is: `fetchUserInfo`
  // only runs after a true `is-logged-in`, so redux holds no profile.
  const signOutOnly = React.useMemo(
    () => [
      {
        id: "nav-sign-out-link",
        label: intl.formatMessage({ defaultMessage: "Sign out" }),
        onClick: (handleClose: () => void) => {
          handleClose()
          handleSignOut()
        }
      }
    ],
    [intl, handleSignOut]
  )

  // First, and before the spinner: once a caller has been told they have no session, nothing
  // may put them back on the loading state.
  if (sessionRejected) {
    return <>{unauthenticatedFallback}</>
  }

  if (sessionProbe.isInitialLoading) {
    return (
      <Box
        role="status"
        aria-label={intl.formatMessage({ defaultMessage: "Loading…" })}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", blockSize: "60vh" }}
      >
        <Progress />
      </Box>
    )
  }

  // ── An external-storefront member is never billed through Carrot/Stripe ─────────────────
  // This mount has no redux users/me, so it reads the storefront off the session probe it already made
  // (`getStatus` carries carrot-app's `consumerSubscription`). A lapsed member is sent to their store's way
  // back in — the same hand-off login makes. Anyone else gets their store's own subscription and order links
  // where the Stripe card would be: account settings, where the in-chrome page sends them, is not reachable
  // without an app session.
  const consumerSubscription = sessionProbe.data?.consumerSubscription
  if (isLapsedExternalStorefrontMember(consumerSubscription)) {
    return <LapsedExternalStorefrontHandoff subscription={consumerSubscription} />
  }
  const externalStorefront =
    consumerSubscription && isExternalStorefrontMember(consumerSubscription) ? consumerSubscription.storefront : null

  return (
    <Box
      component="main"
      sx={(theme) => ({
        inlineSize: "100%",
        maxInlineSize: CONTENT_MAX_INLINE_SIZE,
        marginInline: "auto",
        paddingInline: { xs: theme.spacing(theme.tokens.spacing.md), md: theme.spacing(theme.tokens.spacing.xl) },
        paddingBlock: theme.spacing(theme.tokens.spacing.xl)
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        <UserDropdown
          name={intl.formatMessage({ defaultMessage: "Account" })}
          initials=""
          navigationLinks={signOutOnly}
        />
      </Stack>
      {/* The page's own title and rule, which `PageHeader` would otherwise supply. Kept
          because a page with no chrome and no heading reads as a fragment that failed to
          load, and this one is the member's whole surface. */}
      <Typography variant="h1" component="h1">
        {intl.formatMessage({ defaultMessage: "Billing" })}
      </Typography>
      <Divider sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) })} />
      <Box sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.lg) })}>
        {externalStorefront ? (
          <ExternalStorefrontSubscriptionLinks storefront={externalStorefront} />
        ) : (
          <React.Suspense
            fallback={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  blockSize: 200
                }}
              >
                <Progress />
              </Box>
            }
          >
            <ConsumerSubscriptionBillingRoute
              isLoggedIn={true}
              onSubscriptionReactivated={handleReactivated}
              aeonikFontUrls={AEONIK_FONT_URLS}
            />
          </React.Suspense>
        )}
      </Box>
    </Box>
  )
}
